import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Eye, EyeOff, Lock, Unlock, Layers, Type, Clock, Cloud,
  Image as ImageIcon, Vote, Code2, ChevronUp, ChevronDown, Film, Upload,
  Music2, User, Calendar, SkipForward, Play, Pause,
} from "lucide-react";
import APNGLibraryPicker from "@/components/APNGLibraryPicker";

const TYPE_ICON = {
  logo: Layers,
  ticker: Type,
  lower_third: Type,
  fullscreen: ImageIcon,
  clock: Clock,
  weather: Cloud,
  poll: Vote,
  html: Code2,
  image: ImageIcon,
  text: Type,
  apng: Film,
  song_title: Music2,
  song_artist: User,
  song_year: Calendar,
  next_song: SkipForward,
};

/** Types the user can convert a layer slot into. Kept in sync with backend
 *  GraphicType literal (see /app/backend/models.py). */
const TYPE_OPTIONS = [
  { value: "logo", label: "Logo bug" },
  { value: "ticker", label: "Ticker" },
  { value: "lower_third", label: "Lower third" },
  { value: "song_title", label: "Song title (live)" },
  { value: "song_artist", label: "Artist name (live)" },
  { value: "song_year", label: "Song year (live)" },
  { value: "next_song", label: "Next song preview (live)" },
  { value: "fullscreen", label: "Fullscreen alert" },
  { value: "clock", label: "Live clock" },
  { value: "weather", label: "Weather badge" },
  { value: "poll", label: "Poll snippet" },
  { value: "html", label: "HTML overlay" },
  { value: "image", label: "Static image" },
  { value: "text", label: "Plain text" },
  { value: "apng", label: "Animated PNG (APNG / animated CG)" },
];

const DYN_SONG_TYPES = ["song_title", "song_artist", "song_year", "next_song"];

/** Upload a layer image/APNG to object storage (R2) and return the updated
 *  layer (with props.image_url set to the public URL). */
async function uploadLayerImage(layerId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post(`/graphics/${layerId}/image`, fd);
  return r.data;
}

/** JS mirror of backend _song_flash_windows — N flashes, first at in_offset,
 *  last ending at dur-end_offset. Keeps the canvas preview in exact lockstep
 *  with the on-air burn-in. */
function songFlashWindows(dur, count, d, inOff, endOff) {
  count = Math.max(1, Math.min(Math.round(count || 2), 5));
  d = Math.max(2, Math.min(Number(d) || 10, 60));
  const first = Math.max(0, Number(inOff) || 0);
  const last = dur - (Number(endOff) || 0) - d;
  if (count === 1 || last <= first) {
    if (dur >= first + d) return [[first, first + d]];
    if (dur > d + 1) return [[Math.max(0, dur - d - 1), dur - 1]];
    return [];
  }
  const step = (last - first) / (count - 1);
  return Array.from({ length: count }, (_, i) => [first + i * step, first + i * step + d]);
}

/** Resolve a dynamic song/next layer's state at the mock playhead time:
 *  its live text, whether it is on, and its entrance animation. */
function dynLayerState(layer, pv) {
  const type = layer.type;
  const p = layer.props || {};
  const tm = p.timing || {};
  const t = pv.t;
  const dur = Math.max(1, Number(pv.duration) || 1);
  let text = "";
  if (type === "song_title") text = pv.title;
  else if (type === "song_artist") text = pv.artist;
  else if (type === "song_year") text = pv.year ? String(pv.year) : "";
  else if (type === "next_song") {
    const prefix = p.prefix ?? "Up Next: ";
    const bits = [pv.nextArtist, pv.nextTitle].filter(Boolean);
    text = prefix + bits.join(" — ");
  }
  let windows;
  if (type === "next_song") {
    const lead = Number(tm.lead ?? 15);
    const s = Math.max(0, dur - lead);
    windows = s < dur ? [[s, dur]] : [];
  } else {
    windows = songFlashWindows(
      dur,
      Number(tm.flash_count ?? 2),
      Number(tm.flash_dur ?? 10),
      Number(tm.in_offset ?? 10),
      Number(tm.end_offset ?? 10),
    );
  }
  const active = windows.some(([s, e]) => t >= s && t < e);
  return { text, active, anim: p.anim || "fade" };
}

const CANVAS_W_LOGICAL = 1920;
const CANVAS_H_LOGICAL = 1080;

/** Live tick used by clock/weather/etc. so the canvas feels alive. */
function useNowTick(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Byte-sniff: look for the `acTL` chunk (APNG animation control) inside the
 *  first ~2 KB of a PNG file. Returns true if the file is an animated PNG,
 *  false if it's a static PNG, null on other formats. */
async function isAnimatedPng(file) {
  const buf = await file.slice(0, 2048).arrayBuffer();
  const bytes = new Uint8Array(buf);
  // PNG signature: 89 50 4E 47 0D 0A 1A 0A
  if (bytes[0] !== 0x89 || bytes[1] !== 0x50 || bytes[2] !== 0x4e || bytes[3] !== 0x47) return null;
  // Search for "acTL" (0x61 0x63 0x54 0x4C) chunk
  for (let i = 8; i < bytes.length - 4; i++) {
    if (bytes[i] === 0x61 && bytes[i + 1] === 0x63 && bytes[i + 2] === 0x54 && bytes[i + 3] === 0x4c) {
      return true;
    }
  }
  return false;
}

/** Property panel for the `apng` layer type: URL / file drop, autoplay,
 *  paused (pre-load), on-air only, fade in/out, notes. */
function ApngLayerProps({ sel, patch }) {
  const [dropError, setDropError] = React.useState(null);
  const [dropInfo, setDropInfo] = React.useState(null);
  const [libraryOpen, setLibraryOpen] = React.useState(false);
  const fileRef = React.useRef(null);
  const props = sel.props || {};

  const onFile = async (file) => {
    setDropError(null);
    setDropInfo(null);
    if (!file) return;
    if (!/\.png$/i.test(file.name) && file.type !== "image/png" && file.type !== "image/apng") {
      setDropError("Please choose a .png file");
      return;
    }
    if (file.size > 5_000_000) {
      setDropError(`File is ${(file.size / 1024).toFixed(0)} KB — max 5 MB`);
      return;
    }
    const animated = await isAnimatedPng(file);
    try {
      setDropInfo("Uploading to object storage…");
      const updated = await uploadLayerImage(sel.id, file);
      await patch(sel.id, {
        props: {
          ...props,
          image_url: updated.props?.image_url,
          is_animated: animated === true,
          file_name: file.name,
          file_size_bytes: file.size,
        },
      });
      setDropInfo(animated === true ? "Uploaded · animated PNG" : animated === false ? "Uploaded · static PNG (will play as still image)" : "Uploaded to object storage");
    } catch (e) {
      setDropError("Upload failed: " + (e?.response?.data?.detail || e.message || "unknown error"));
    }
  };

  return (
    <div className="space-y-3" data-testid="apng-props">
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">APNG URL (object storage)</Label>
        <Input
          value={props.image_url && props.image_url.length < 200 ? props.image_url : (props.image_url ? "(stored image · use Upload to replace)" : "")}
          onChange={(e) => {
            const v = e.target.value;
            if (v.startsWith("data:")) { setDropError("Embedded data is not allowed — use Upload for files."); return; }
            patch(sel.id, { props: { ...props, image_url: v } });
          }}
          className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs font-mono"
          placeholder="https://cdn.example/animated.png"
          data-testid="apng-url-input"
        />
      </div>
      <div>
        <input
          ref={fileRef}
          type="file"
          accept=".png,image/png,image/apng"
          onChange={(e) => onFile(e.target.files?.[0])}
          className="hidden"
          data-testid="apng-file-input"
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setLibraryOpen(true)}
            data-testid="apng-library-open-btn"
            className="font-mono text-[10px] uppercase tracking-widest px-2 py-1.5 rounded-sm border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 flex items-center justify-center gap-1.5"
          >
            <Layers className="w-3 h-3" /> Library
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="apng-upload-btn"
            className="font-mono text-[10px] uppercase tracking-widest px-2 py-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center justify-center gap-1.5"
          >
            <Upload className="w-3 h-3" /> Upload
          </button>
        </div>
        {dropError && <div className="text-[10px] text-red-400 mt-1 font-mono" data-testid="apng-error">{dropError}</div>}
        {dropInfo && <div className="text-[10px] text-emerald-400 mt-1 font-mono" data-testid="apng-info">{dropInfo}</div>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center gap-1.5 text-xs text-zinc-300">
          <input
            type="checkbox"
            checked={props.apng_autoplay !== false}
            onChange={(e) => patch(sel.id, { props: { ...props, apng_autoplay: e.target.checked } })}
            className="accent-emerald-500"
            data-testid="apng-autoplay"
          />
          Autoplay on air
        </label>
        <label className="flex items-center gap-1.5 text-xs text-zinc-300">
          <input
            type="checkbox"
            checked={!!props.apng_paused}
            onChange={(e) => patch(sel.id, { props: { ...props, apng_paused: e.target.checked } })}
            className="accent-amber-500"
            data-testid="apng-paused"
          />
          Pre-load paused
        </label>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Show duration (s, 0 = loop forever)</Label>
        <Input
          type="number"
          min="0"
          step="1"
          value={props.apng_duration_sec ?? 0}
          onChange={(e) => patch(sel.id, { props: { ...props, apng_duration_sec: Number(e.target.value) } })}
          className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs font-mono"
          data-testid="apng-duration"
        />
      </div>
      <div className="text-[10px] font-mono text-zinc-500 leading-relaxed">
        Broadcasters use APNG for: animated channel bugs, lower-third slides, show intros, stinger transitions. Browsers play APNG natively via &lt;img&gt; — no plugin needed.
      </div>
      {props.is_animated === false && props.image_url && (
        <div className="text-[10px] font-mono text-amber-400" data-testid="apng-static-warning">
          ⚠ This PNG is static — it will show as a still image. For motion, use an APNG-encoded file (e.g. from ezgif.com or ffmpeg with `-plays 0`).
        </div>
      )}
      <APNGLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(asset) => patch(sel.id, {
          props: {
            ...props,
            image_url: asset.image_url,
            is_animated: !!asset.is_animated,
            file_name: asset.file_name,
            file_size_bytes: asset.file_size_bytes,
            library_asset_id: asset.id,
            library_asset_name: asset.name,
          },
        })}
      />
    </div>
  );
}


/** Logo / static-image artwork field: paste a URL (no data: blobs) or upload
 *  a file straight to object storage (R2). Stores only the URL in the DB. */
function ImageUrlField({ sel, patch }) {
  const props = sel.props || {};
  const fileRef = React.useRef(null);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setErr(null);
    if (file.size > 5_000_000) { setErr("Image too large (max 5 MB)"); return; }
    setBusy(true);
    try {
      const updated = await uploadLayerImage(sel.id, file);
      await patch(sel.id, { props: { ...props, image_url: updated.props?.image_url } });
    } catch (e) {
      setErr("Upload failed: " + (e?.response?.data?.detail || e.message || "error"));
    } finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Image (object storage)</Label>
      <Input
        value={props.image_url && props.image_url.length < 200 ? props.image_url : (props.image_url ? "(stored image · use Upload to replace)" : "")}
        onChange={(e) => {
          const v = e.target.value;
          if (v.startsWith("data:")) { setErr("Embedded data is not allowed — use Upload for files."); return; }
          patch(sel.id, { props: { ...props, image_url: v } });
        }}
        className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs font-mono"
        placeholder="https://…"
        data-testid="image-url-input"
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
        data-testid="image-file-input"
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="image-upload-btn"
        className="mt-2 w-full font-mono text-[10px] uppercase tracking-widest px-2 py-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center justify-center gap-1.5 disabled:opacity-50"
      >
        <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Upload image"}
      </button>
      {err && <div className="text-[10px] text-red-400 mt-1 font-mono" data-testid="image-upload-error">{err}</div>}
      {props.image_url && (
        <div className="mt-2 h-16 bg-zinc-950 border border-zinc-800 rounded-sm flex items-center justify-center overflow-hidden">
          <img src={props.image_url} alt="preview" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}

/** Editable timing + style controls for the live song/next-song layers. */
function DynamicLayerProps({ sel, patch }) {
  const props = sel.props || {};
  const tm = props.timing || {};
  const setTiming = (k, v) => patch(sel.id, { props: { ...props, timing: { ...tm, [k]: v } } });
  const isNext = sel.type === "next_song";
  return (
    <div className="space-y-3" data-testid="dyn-layer-props">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Text color</Label>
          <input
            type="color"
            value={props.color || "#FFFFFF"}
            onChange={(e) => patch(sel.id, { props: { ...props, color: e.target.value } })}
            className="mt-1 w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm"
            data-testid="dyn-color"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Box color</Label>
          <input
            type="color"
            value={props.background || "#111111"}
            onChange={(e) => patch(sel.id, { props: { ...props, background: e.target.value } })}
            className="mt-1 w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm"
            data-testid="dyn-bg"
          />
        </div>
      </div>
      <label className="flex items-center gap-1.5 text-xs text-zinc-300">
        <input
          type="checkbox"
          checked={props.box !== false}
          onChange={(e) => patch(sel.id, { props: { ...props, box: e.target.checked } })}
          className="accent-emerald-500"
          data-testid="dyn-box"
        />
        Show background box
      </label>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Entrance style</Label>
        <select
          value={props.anim || "fade"}
          onChange={(e) => patch(sel.id, { props: { ...props, anim: e.target.value } })}
          className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs px-2 py-1.5 font-mono"
          data-testid="dyn-anim"
        >
          <option value="fade">Fade in / out</option>
          <option value="slide">Slide in from left</option>
        </select>
      </div>
      {isNext ? (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Prefix text</Label>
            <Input
              value={props.prefix ?? "Up Next: "}
              onChange={(e) => patch(sel.id, { props: { ...props, prefix: e.target.value } })}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
              data-testid="dyn-prefix"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Lead before end (s)</Label>
            <Input
              type="number" min="1" step="1"
              value={tm.lead ?? 15}
              onChange={(e) => setTiming("lead", Number(e.target.value))}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
              data-testid="dyn-lead"
            />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">In-offset (s)</Label>
            <Input type="number" min="0" step="1" value={tm.in_offset ?? 10}
              onChange={(e) => setTiming("in_offset", Number(e.target.value))}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid="dyn-in-offset" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">End-offset (s)</Label>
            <Input type="number" min="0" step="1" value={tm.end_offset ?? 10}
              onChange={(e) => setTiming("end_offset", Number(e.target.value))}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid="dyn-end-offset" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Flash count</Label>
            <Input type="number" min="1" max="5" step="1" value={tm.flash_count ?? 2}
              onChange={(e) => setTiming("flash_count", Number(e.target.value))}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid="dyn-flash-count" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Flash duration (s)</Label>
            <Input type="number" min="2" max="60" step="1" value={tm.flash_dur ?? 10}
              onChange={(e) => setTiming("flash_dur", Number(e.target.value))}
              className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid="dyn-flash-dur" />
          </div>
        </div>
      )}
      <div className="text-[10px] font-mono text-zinc-500 leading-relaxed">
        {isNext
          ? "Auto-fills with the next clip's artist & title, shown for the lead seconds before each song ends."
          : "Auto-fills from the now-playing clip. Default: two 10s flashes — ~10s in and ~10s before the end."}
      </div>
    </div>
  );
}

/**
 * Render a single layer visually — text, ticker (marquee), image, HTML,
 * live clock, weather badge, poll snippet, lower-third, logo, fullscreen alert.
 */
function LayerContent({ layer, now, scale, preview }) {
  const { type, props = {}, name, opacity } = layer;
  const commonStyle = { opacity, width: "100%", height: "100%" };
  if (type === "clock") {
    const fmt = props.format === "HH:MM:SS" ? "HH:MM:SS" : "HH:MM";
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return (
      <div className="w-full h-full flex items-center justify-center bg-black/70 border border-white/10" style={commonStyle}>
        <span className="font-display font-black text-white" style={{ fontSize: 36 * scale }}>
          {fmt === "HH:MM:SS" ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`}
        </span>
      </div>
    );
  }
  if (type === "ticker") {
    const text = props.text || "Breaking news · Live ticker · Configure text in properties panel";
    return (
      <div className="w-full h-full bg-red-600/90 text-white overflow-hidden flex items-center" style={commonStyle}>
        <div className="whitespace-nowrap animate-[marquee_18s_linear_infinite]" style={{ fontSize: 20 * scale, paddingLeft: "100%" }}>
          {`${text}   •   ${text}`}
        </div>
      </div>
    );
  }
  if (type === "lower_third") {
    const title = props.title || props.text || "Host Name";
    const subtitle = props.subtitle || "Role · Show";
    return (
      <div className="w-full h-full flex flex-col justify-center px-3" style={{ ...commonStyle, background: "linear-gradient(90deg, rgba(0,0,0,0.85), rgba(0,0,0,0.4))" }}>
        <div className="font-display font-bold text-white" style={{ fontSize: 22 * scale }}>{title}</div>
        <div className="text-white/70 font-mono" style={{ fontSize: 12 * scale }}>{subtitle}</div>
      </div>
    );
  }
  if (type === "logo") {
    const text = props.text || props.title || "LOGO";
    return (
      <div className="w-full h-full flex items-center justify-center bg-black/70 border border-white/20" style={commonStyle}>
        {props.image_url ? (
          <img src={props.image_url} alt={text} className="max-w-full max-h-full object-contain" />
        ) : (
          <span className="font-display font-black text-white" style={{ fontSize: 26 * scale }}>{text}</span>
        )}
      </div>
    );
  }
  if (type === "image") {
    if (props.image_url) {
      return <img src={props.image_url} alt={name} className="w-full h-full object-cover" style={commonStyle} />;
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-zinc-800 border border-white/10 text-zinc-500 font-mono text-[10px]" style={commonStyle}>
        {name}
      </div>
    );
  }
  if (type === "apng") {
    // Animated PNG — the browser handles APNG playback + looping natively via
    // <img>. The `apng_autoplay` flag lets an operator pre-arm a bug/lower-
    // third that only shows when the on-air bit is set (driven by parent).
    if (!props.image_url) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-zinc-900 border border-dashed border-emerald-500/50 text-emerald-400/80 font-mono text-[10px]" style={commonStyle}>
          <div className="text-center leading-tight px-2">
            <div className="uppercase tracking-widest text-[9px]">APNG · empty</div>
            <div className="text-zinc-500 mt-1">paste an APNG URL →</div>
          </div>
        </div>
      );
    }
    const paused = props.apng_paused === true;
    // "paused" hides the layer (freezes) so the operator can pre-load without
    // showing motion until they're ready. Using object-fit contain preserves
    // pixel-perfect animation for lower thirds / channel bugs / show intros.
    return (
      <img
        src={props.image_url}
        alt={name}
        className="w-full h-full object-contain"
        style={{
          ...commonStyle,
          filter: paused ? "grayscale(0.6) brightness(0.5)" : undefined,
          transition: "filter 0.2s linear",
        }}
        data-testid="apng-layer-render"
      />
    );
  }
  if (type === "html") {
    return (
      <div
        className="w-full h-full overflow-hidden"
        style={commonStyle}
        dangerouslySetInnerHTML={{ __html: props.html || `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);color:#fff;font-family:monospace;">HTML LAYER</div>` }}
      />
    );
  }
  if (type === "weather") {
    return (
      <div className="w-full h-full flex items-center gap-2 px-2 bg-blue-900/80 border border-white/10" style={commonStyle}>
        <Cloud className="w-4 h-4 text-white" />
        <span className="font-display text-white" style={{ fontSize: 18 * scale }}>
          {props.city || "Your City"} · 24°
        </span>
      </div>
    );
  }
  if (type === "poll") {
    return (
      <div className="w-full h-full p-2 bg-black/80 border border-emerald-400/40" style={commonStyle}>
        <div className="text-emerald-300 font-mono uppercase" style={{ fontSize: 10 * scale }}>Poll</div>
        <div className="text-white" style={{ fontSize: 14 * scale }}>{props.question || "Vote now"}</div>
      </div>
    );
  }
  if (type === "fullscreen") {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-900/80 border-2 border-red-500 text-white font-display font-black" style={{ ...commonStyle, fontSize: 32 * scale }}>
        {props.text || "BREAKING NEWS"}
      </div>
    );
  }
  if (type === "song_title" || type === "song_artist" || type === "song_year" || type === "next_song") {
    const sample = {
      song_title: "Song Title",
      song_artist: "Artist Name",
      song_year: "2024",
      next_song: (props.prefix ?? "Up Next: ") + "Artist — Title",
    }[type];
    const bg = props.background || "rgba(0,0,0,0.78)";
    // Live preview: use the mock now-playing metadata + exact flash timing so
    // operators see placement + in/out behaviour before going on air.
    const st = preview ? dynLayerState(layer, preview) : null;
    const text = st ? (st.text || sample) : sample;
    const boxStyle = {
      ...commonStyle, background: bg, color: props.color || "#FFFFFF",
      transition: "opacity 0.3s linear, transform 0.35s ease-out",
    };
    if (st) {
      boxStyle.opacity = st.active ? opacity : 0;
      if (st.anim === "slide") boxStyle.transform = st.active ? "translateX(0)" : "translateX(-115%)";
    }
    return (
      <div
        className="w-full h-full flex items-center px-3 border border-white/10"
        style={boxStyle}
        data-testid={`dyn-layer-preview-${type}`}
      >
        <span className="font-display font-bold truncate" style={{ fontSize: 20 * scale }}>{text}</span>
        {!preview && (
          <span className="ml-auto pl-2 text-[9px] font-mono uppercase tracking-widest text-white/40">live</span>
        )}
      </div>
    );
  }
  // Default: text
  return (
    <div className="w-full h-full flex items-center px-2 bg-black/60 text-white" style={commonStyle}>
      <span className="font-mono" style={{ fontSize: 14 * scale }}>{props.text || name}</span>
    </div>
  );
}

export default function GraphicsEditor() {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [layers, setLayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [canvasWidth, setCanvasWidth] = useState(960);
  // Live preview playhead: a mock now-playing song so operators can see the
  // exact flash timing + placement (and fade/slide) before going on air.
  const [preview, setPreview] = useState({
    playing: true, t: 0, duration: 200,
    title: "Djadja", artist: "Aya Nakamura", year: 2019,
    nextTitle: "Dis Moi", nextArtist: "Niska",
  });
  const canvasRef = useRef(null);
  const dragRef = useRef(null); // { mode: 'move'|'resize', layerId, startX, startY, orig }
  const now = useNowTick(1000);

  useEffect(() => {
    api.get("/channels").then((r) => {
      setChannels(r.data);
      if (r.data[0]) setChannelId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!channelId) return;
    api.get("/graphics", { params: { channel_id: channelId } }).then((r) => {
      setLayers(r.data);
      setSelected(r.data[0]?.id || null);
    });
  }, [channelId]);

  // Track canvas actual rendered width for accurate drag math on resize.
  useEffect(() => {
    if (!canvasRef.current) return;
    const el = canvasRef.current;
    const measure = () => setCanvasWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [channelId]);

  // Advance the mock playhead ~4x/sec while playing; loops at song length.
  useEffect(() => {
    if (!preview.playing) return undefined;
    const id = setInterval(() => {
      setPreview((p) => ({ ...p, t: (p.t + 0.25) % Math.max(1, Number(p.duration) || 1) }));
    }, 250);
    return () => clearInterval(id);
  }, [preview.playing, preview.duration]);

  const scale = canvasWidth / CANVAS_W_LOGICAL;
  const canvasHeight = CANVAS_H_LOGICAL * scale;

  const sel = layers.find((l) => l.id === selected);

  // ---- Persistence ----
  const patch = async (id, body) => {
    setLayers((p) => p.map((l) => (l.id === id ? { ...l, ...body } : l)));
    try { await api.patch(`/graphics/${id}`, body); } catch { /* noop */ }
  };
  const toggle = async (id) => {
    const l = layers.find((x) => x.id === id);
    if (l?.locked) return;
    await patch(id, { visible: !l.visible });
  };
  const lockToggle = async (id) => {
    const l = layers.find((x) => x.id === id);
    await patch(id, { locked: !l.locked });
  };

  // Reorder z-index: swap with neighbour in z_index order.
  const reorder = async (id, dir) => {
    const sorted = [...layers].sort((a, b) => a.z_index - b.z_index);
    const idx = sorted.findIndex((l) => l.id === id);
    const swap = dir === "up" ? sorted[idx + 1] : sorted[idx - 1];
    if (!swap) return;
    const a = sorted[idx];
    await Promise.all([
      patch(a.id, { z_index: swap.z_index }),
      patch(swap.id, { z_index: a.z_index }),
    ]);
  };

  // ---- Drag / Resize on canvas ----
  const onLayerMouseDown = (e, layer, mode = "move") => {
    e.stopPropagation();
    e.preventDefault();
    if (layer.locked) return;
    setSelected(layer.id);
    dragRef.current = {
      mode,
      layerId: layer.id,
      startX: e.clientX,
      startY: e.clientY,
      orig: { x: layer.x, y: layer.y, width: layer.width, height: layer.height },
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dxLogical = (ev.clientX - d.startX) / scale;
      const dyLogical = (ev.clientY - d.startY) / scale;
      setLayers((prev) =>
        prev.map((l) => {
          if (l.id !== d.layerId) return l;
          if (d.mode === "move") {
            const x = Math.max(0, Math.min(CANVAS_W_LOGICAL - l.width, d.orig.x + dxLogical));
            const y = Math.max(0, Math.min(CANVAS_H_LOGICAL - l.height, d.orig.y + dyLogical));
            return { ...l, x, y };
          }
          // resize (bottom-right)
          const width = Math.max(40, Math.min(CANVAS_W_LOGICAL - l.x, d.orig.width + dxLogical));
          const height = Math.max(24, Math.min(CANVAS_H_LOGICAL - l.y, d.orig.height + dyLogical));
          return { ...l, width, height };
        })
      );
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current;
      dragRef.current = null;
      if (!d) return;
      // Persist via patch using current state snapshot
      setLayers((prev) => {
        const cur = prev.find((x) => x.id === d.layerId);
        if (cur) {
          api.patch(`/graphics/${cur.id}`, {
            x: Math.round(cur.x), y: Math.round(cur.y),
            width: Math.round(cur.width), height: Math.round(cur.height),
          }).catch(() => { /* noop */ });
        }
        return prev;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4 shrink-0">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ 14-Layer Graphics Editor</div>
          <h1 className="font-display font-bold text-2xl mt-1">Graphics compositor</h1>
        </div>
        <Select value={channelId} onValueChange={setChannelId}>
          <SelectTrigger className="w-56 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="graphics-channel-select">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            {channels.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-[260px_1fr_300px] gap-3 flex-1 min-h-0">
        {/* Properties */}
        <div className="panel flex flex-col overflow-hidden">
          <div className="panel-header"><span className="panel-title">Properties</span></div>
          <div className="p-3 overflow-y-auto scrollbar-thin flex-1 space-y-3">
            {sel ? (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Layer type</Label>
                  <select
                    value={sel.type}
                    onChange={(e) => patch(sel.id, { type: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs px-2 py-1.5 font-mono"
                    data-testid="layer-type-select"
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Name</Label>
                  <Input
                    value={sel.name}
                    onChange={(e) => patch(sel.id, { name: e.target.value })}
                    className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
                    data-testid="layer-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">X</Label>
                    <Input type="number" value={Math.round(sel.x)} onChange={(e) => patch(sel.id, { x: Number(e.target.value) })} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid="layer-x" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Y</Label>
                    <Input type="number" value={Math.round(sel.y)} onChange={(e) => patch(sel.id, { y: Number(e.target.value) })} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid="layer-y" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">W</Label>
                    <Input type="number" value={Math.round(sel.width)} onChange={(e) => patch(sel.id, { width: Number(e.target.value) })} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">H</Label>
                    <Input type="number" value={Math.round(sel.height)} onChange={(e) => patch(sel.id, { height: Number(e.target.value) })} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Opacity ({(sel.opacity * 100).toFixed(0)}%)</Label>
                  <input
                    type="range" min="0" max="1" step="0.05" value={sel.opacity}
                    onChange={(e) => patch(sel.id, { opacity: Number(e.target.value) })}
                    className="w-full mt-1"
                    data-testid="layer-opacity"
                  />
                </div>
                {(sel.type === "ticker" || sel.type === "text" || sel.type === "logo" || sel.type === "fullscreen" || sel.type === "lower_third") && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Text</Label>
                    <Input
                      value={sel.props?.text || sel.props?.title || ""}
                      onChange={(e) => patch(sel.id, { props: { ...sel.props, text: e.target.value } })}
                      className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
                      data-testid="layer-text-prop"
                    />
                  </div>
                )}
                {sel.type === "lower_third" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Subtitle</Label>
                    <Input
                      value={sel.props?.subtitle || ""}
                      onChange={(e) => patch(sel.id, { props: { ...sel.props, subtitle: e.target.value } })}
                      className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
                    />
                  </div>
                )}
                {(sel.type === "image" || sel.type === "logo") && (
                  <ImageUrlField sel={sel} patch={patch} />
                )}
                {DYN_SONG_TYPES.includes(sel.type) && (
                  <DynamicLayerProps sel={sel} patch={patch} />
                )}
                {sel.type === "apng" && (
                  <ApngLayerProps sel={sel} patch={patch} />
                )}
                {sel.type === "clock" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Format</Label>
                    <select
                      value={sel.props?.format || "HH:MM"}
                      onChange={(e) => patch(sel.id, { props: { ...sel.props, format: e.target.value } })}
                      className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded-sm text-xs px-2 py-1"
                    >
                      <option value="HH:MM">HH:MM</option>
                      <option value="HH:MM:SS">HH:MM:SS</option>
                    </select>
                  </div>
                )}
                {sel.type === "weather" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">City</Label>
                    <Input
                      value={sel.props?.city || ""}
                      onChange={(e) => patch(sel.id, { props: { ...sel.props, city: e.target.value } })}
                      className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
                    />
                  </div>
                )}
                {sel.type === "html" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">HTML</Label>
                    <textarea
                      value={sel.props?.html || ""}
                      onChange={(e) => patch(sel.id, { props: { ...sel.props, html: e.target.value } })}
                      className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded-sm p-2 font-mono text-[10px] h-24"
                      data-testid="layer-html-prop"
                    />
                  </div>
                )}
                <div className="pt-2 border-t border-zinc-800 flex items-center gap-1">
                  <button
                    onClick={() => reorder(sel.id, "up")}
                    className="flex-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-800 text-zinc-300 hover:border-zinc-600"
                    data-testid="layer-z-up"
                  >
                    <ChevronUp className="w-3 h-3 inline" /> up
                  </button>
                  <button
                    onClick={() => reorder(sel.id, "down")}
                    className="flex-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-800 text-zinc-300 hover:border-zinc-600"
                    data-testid="layer-z-down"
                  >
                    <ChevronDown className="w-3 h-3 inline" /> down
                  </button>
                </div>
                <div className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">
                  z-index {sel.z_index}
                </div>
              </>
            ) : (
              <div className="text-xs text-zinc-500 italic">Select a layer</div>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="panel flex flex-col overflow-hidden">
          <div className="panel-header">
            <span className="panel-title">Canvas · 1920×1080</span>
            <span className="font-mono text-[10px] text-zinc-500">
              {layers.filter((l) => l.visible).length} of 14 layers visible · drag to move · corner to resize
            </span>
          </div>
          <div className="flex-1 bg-black dotted-grid p-4 overflow-auto scrollbar-thin flex items-center justify-center">
            <div
              ref={canvasRef}
              className="relative bg-[#0A0F2C] dotted-grid w-full max-w-[1200px]"
              style={{ aspectRatio: "16 / 9" }}
              data-testid="graphics-canvas"
              onClick={() => setSelected(null)}
            >
              {/* Base placeholder */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="font-display font-black text-4xl text-zinc-700">PROGRAM VIDEO</div>
              </div>
              {/* Layers */}
              {layers
                .filter((l) => l.visible)
                .sort((a, b) => a.z_index - b.z_index)
                .map((l) => {
                  const isSel = selected === l.id;
                  const style = {
                    position: "absolute",
                    left: l.x * scale,
                    top: l.y * scale,
                    width: l.width * scale,
                    height: l.height * scale,
                    cursor: l.locked ? "not-allowed" : "move",
                    outline: isSel
                      ? "1.5px dashed #EF4444"
                      : "1px solid rgba(255,255,255,0.12)",
                    outlineOffset: "-1px",
                    zIndex: l.z_index,
                  };
                  return (
                    <div
                      id={`layer-${l.id}`}
                      key={l.id}
                      style={style}
                      onMouseDown={(e) => onLayerMouseDown(e, l, "move")}
                      onClick={(e) => { e.stopPropagation(); setSelected(l.id); }}
                      data-testid={`canvas-layer-${l.id}`}
                    >
                      <LayerContent layer={l} now={now} scale={scale} preview={preview} />
                      {isSel && !l.locked && (
                        <div
                          onMouseDown={(e) => onLayerMouseDown(e, l, "resize")}
                          data-testid={`canvas-resize-${l.id}`}
                          className="absolute right-0 bottom-0 w-3 h-3 bg-red-500 cursor-se-resize"
                          style={{ transform: "translate(50%,50%)" }}
                        />
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
          {/* Live preview controls — mock now-playing song to time the layers */}
          <div className="border-t border-zinc-800 p-2 space-y-2" data-testid="preview-controls">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreview((p) => ({ ...p, playing: !p.playing }))}
                data-testid="preview-play-toggle"
                className="shrink-0 w-7 h-7 rounded-sm border border-zinc-700 text-zinc-200 hover:bg-zinc-800 flex items-center justify-center"
                title={preview.playing ? "Pause preview" : "Play preview"}
              >
                {preview.playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <input
                type="range" min="0" max={Math.max(1, Number(preview.duration) || 1)} step="0.5"
                value={preview.t}
                onChange={(e) => setPreview((p) => ({ ...p, t: Number(e.target.value), playing: false }))}
                className="flex-1 accent-red-500"
                data-testid="preview-scrub"
              />
              <span className="font-mono text-[10px] text-zinc-400 w-24 text-right tabular-nums" data-testid="preview-time">
                {preview.t.toFixed(1)}s / {Number(preview.duration) || 0}s
              </span>
            </div>
            <div className="grid grid-cols-6 gap-1.5">
              {[
                { k: "title", label: "Title", type: "text" },
                { k: "artist", label: "Artist", type: "text" },
                { k: "year", label: "Year", type: "number" },
                { k: "duration", label: "Length s", type: "number" },
                { k: "nextArtist", label: "Next artist", type: "text" },
                { k: "nextTitle", label: "Next title", type: "text" },
              ].map((f) => (
                <div key={f.k}>
                  <Label className="text-[9px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
                  <Input
                    type={f.type}
                    value={preview[f.k]}
                    onChange={(e) => setPreview((p) => ({ ...p, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value }))}
                    className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-[11px] h-7"
                    data-testid={`preview-${f.k}`}
                  />
                </div>
              ))}
            </div>
            <div className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">
              Mock playhead · drag to scrub · song/next layers flash exactly as on air
            </div>
          </div>
        </div>

        {/* Layers panel */}
        <div className="panel flex flex-col overflow-hidden">
          <div className="panel-header"><span className="panel-title">Layers (14)</span></div>
          <div className="p-2 overflow-y-auto scrollbar-thin flex-1 space-y-1">
            {[...layers].sort((a, b) => b.layer_index - a.layer_index).map((l) => {
              const Icon = TYPE_ICON[l.type] || Layers;
              return (
                <div
                  key={l.id}
                  onClick={() => setSelected(l.id)}
                  data-testid={`layer-row-${l.layer_index}`}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border cursor-pointer ${
                    selected === l.id ? "border-red-500/60 bg-red-500/5" : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                  }`}
                >
                  <span className="font-mono text-[9px] text-zinc-500 w-6">{String(l.layer_index + 1).padStart(2, "0")}</span>
                  <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" strokeWidth={1.5} />
                  <span className="text-xs flex-1 truncate">{l.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); lockToggle(l.id); }}
                    data-testid={`layer-lock-${l.layer_index}`}
                    className="text-zinc-500 hover:text-white"
                  >
                    {l.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggle(l.id); }}
                    data-testid={`layer-toggle-${l.layer_index}`}
                    className={l.visible ? "text-emerald-400" : "text-zinc-600 hover:text-zinc-300"}
                  >
                    {l.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
